/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { type ProductType } from '@atlassian/loom-utils';

import type { SourceProduct, SuggestionConfigContext } from '../../../types';

import { useLoomVideoConfig } from './hooks/use-loom-video-config';
import { SuggestionItemBase, type SuggestionItemBaseProps } from './SuggestionItemBase';

interface WithLoomVideoConfigProps extends SuggestionItemBaseProps {
	context: SuggestionConfigContext;
	product: SourceProduct;
}

const messages = defineMessages({
	loomVideo: {
		id: 'universal-create.loom-video-menu-item.loom-video',
		defaultMessage: 'Loom Video',
		description: 'Loom video menu item in the create menu',
	},
});

export function convertSourceProductToProductType(product: SourceProduct): ProductType {
	switch (product) {
		case 'townsquare':
			return 'TOWNSQUARE';
		case 'confluence':
			return 'CONFLUENCE';
		case 'teams':
			return 'TEAMS';
		case 'goal':
			return 'GOAL';
		default:
			return 'UNKNOWN';
	}
}

/**
 * Higher-order component that wraps a component with Loom video configuration
 * @param WrappedComponent - The component to wrap with Loom video configuration
 * @returns A new component with Loom video configuration applied
 */
function withLoomVideoConfig<P extends WithLoomVideoConfigProps>(
	WrappedComponent: ComponentType<P>,
): ComponentType<WithLoomVideoConfigProps> {
	return function WithLoomVideoConfigComponent(props: WithLoomVideoConfigProps) {
		const { context, product } = props;
		const { formatMessage } = useIntl();

		const customisedItemConfig = useLoomVideoConfig({
			cloudId: context.cloudId,
			productName: convertSourceProductToProductType(product),
			environment: context.environment || 'prod',
		});

		const finalItemConfig = {
			...customisedItemConfig,
			name: formatMessage(messages.loomVideo),
		};

		return <WrappedComponent {...(props as P)} customisedItemConfig={finalItemConfig} />;
	};
}

/**
 * LoomVideoMenuItem component that renders a Loom video suggestion item
 * Uses the withLoomVideoConfig HOC to apply Loom-specific configuration
 */
export const LoomVideoItem = withLoomVideoConfig(SuggestionItemBase);
